'use client';

/**
 * ManualSyncButton — operator-triggered "Refresh from CRM" control.
 *
 * Sits in the dashboard header next to the bell. Clicking it forces an
 * immediate per-tenant Copilot → Supabase sync and busts the Tier-1
 * route cache so the next dashboard poll returns fresh state instead of
 * a 60s-stale cached body.
 *
 * Why this exists: the 20-min auto-sync handles the common case. But if
 * an operator just edited their CRM directly (estimate accepted on a
 * phone, visit rescheduled in Copilot's UI), they want to see CrewSync
 * reflect that in seconds, not in twenty minutes. This button is the
 * escape hatch.
 *
 * Backend is rate-limited to 1 call per tenant per 30s; the UI mirrors
 * that with a disabled-state countdown.
 */

import { useState, useCallback, useEffect } from 'react';
import { RefreshCw, Check, AlertCircle } from 'lucide-react';
import clsx from 'clsx';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3003';

type Status = 'idle' | 'syncing' | 'success' | 'error' | 'cooldown';

type SyncSummary = {
  customers: number | null;
  services: number | null;
  estimates: number | null;
  invoices: number | null;
  visits: number | null;
  elapsedMs: number;
};

async function getAuthToken(): Promise<string | null> {
  try {
    const mod = await import('@/lib/supabase');
    const { data: { session } } = await mod.supabase.auth.getSession();
    return session?.access_token ?? null;
  } catch {
    return null;
  }
}

export default function ManualSyncButton() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState<string>('');
  const [cooldownSec, setCooldownSec] = useState(0);

  // Cooldown countdown after a 429 response. Ticks once per second until
  // it hits zero, then drops the button back to idle.
  useEffect(() => {
    if (status !== 'cooldown' || cooldownSec <= 0) return;
    const t = setTimeout(() => {
      setCooldownSec(s => {
        const next = s - 1;
        if (next <= 0) {
          setStatus('idle');
          setMessage('');
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearTimeout(t);
  }, [status, cooldownSec]);

  // Auto-clear success/error message after a few seconds so the button
  // doesn't sit there with stale text.
  useEffect(() => {
    if (status !== 'success' && status !== 'error') return;
    const t = setTimeout(() => {
      setStatus('idle');
      setMessage('');
    }, 5000);
    return () => clearTimeout(t);
  }, [status]);

  const handleClick = useCallback(async () => {
    if (status !== 'idle') return;
    setStatus('syncing');
    setMessage('');
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_URL}/api/sync/manual`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.status === 429) {
        const body = await res.json().catch(() => ({}));
        const sec = Number(body.retryAfterSec) || 30;
        setStatus('cooldown');
        setCooldownSec(sec);
        setMessage(`Wait ${sec}s before refreshing again.`);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setStatus('error');
        setMessage(body.reason || body.error || `Sync failed (${res.status})`);
        return;
      }
      const body: { ok: boolean; summary?: SyncSummary } = await res.json();
      const s = body.summary;
      if (s) {
        const parts: string[] = [];
        if (s.customers != null) parts.push(`${s.customers} customers`);
        if (s.invoices != null) parts.push(`${s.invoices} invoices`);
        if (s.visits != null) parts.push(`${s.visits} visits`);
        if (s.estimates != null) parts.push(`${s.estimates} estimates`);
        const took = (s.elapsedMs / 1000).toFixed(1);
        setMessage(`Synced ${parts.join(', ')} in ${took}s`);
      } else {
        setMessage('Synced.');
      }
      setStatus('success');
      // Notify any listening dashboard components that a manual sync
      // just completed; if a card wants to fetch faster than its
      // polling interval, it can hook this event.
      try {
        window.dispatchEvent(new CustomEvent('crewsync:manual-sync', { detail: body.summary }));
      } catch { /* noop in test envs */ }
    } catch (err) {
      setStatus('error');
      setMessage(err instanceof Error ? err.message : 'Network error');
    }
  }, [status]);

  const Icon = status === 'success' ? Check
             : status === 'error'   ? AlertCircle
             : RefreshCw;

  const isDisabled = status === 'syncing' || status === 'cooldown';
  const label = status === 'syncing'  ? 'Syncing…'
              : status === 'cooldown' ? `Wait ${cooldownSec}s`
              : status === 'success'  ? 'Synced'
              : status === 'error'    ? 'Sync failed'
              : 'Refresh';

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        disabled={isDisabled}
        title={message || 'Pull fresh data from your CRM right now'}
        aria-label="Refresh data from CRM"
        className={clsx(
          'p-2 rounded-lg transition-colors',
          'text-[--text-secondary] hover:text-[--text-primary] hover:bg-[--bg-main]',
          'disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent',
          status === 'success' && 'text-[#00C2A8]',
          status === 'error' && 'text-red-500',
        )}
      >
        <Icon className={clsx('w-5 h-5', status === 'syncing' && 'animate-spin')} />
        <span className="sr-only">{label}</span>
      </button>
      {message && (status === 'success' || status === 'error' || status === 'cooldown') && (
        <div className="absolute right-0 top-full mt-1 px-3 py-2 bg-[--bg-elevated] rounded-lg shadow-lg text-xs text-[--text-primary] whitespace-nowrap z-50 max-w-[20rem] truncate">
          {message}
        </div>
      )}
    </div>
  );
}
